import type { VideoType } from '@setplex/tria-api'
import type { RewindTypes } from '~/shared/constants/rewind'
import type {
  CurrentEventMediaUrlWithEpg,
  CurrentProgramMediaUrl,
  CurrentTvMediaUrlWithEpg,
} from '~/shared/interfaces'

import { useEffect, useMemo, useState } from 'react'
import { chooseRewind } from '~/shared/helpers/rewind'

export interface IRewindStamps {
  rewoundTargetStartSec: number
  rewoundUrlStartSec: number
}

export interface IRewindDetails extends IRewindStamps {
  rewindType: RewindTypes | undefined
}

export interface IRewindSetStamps extends IRewindStamps {
  setRewindStamps: React.Dispatch<React.SetStateAction<IRewindStamps>>
}

export interface IRewindState {
  isRewound: boolean
  setIsRewound: React.Dispatch<React.SetStateAction<boolean>>
  isRewindDelayed: boolean
  setIsRewindDelayed: React.Dispatch<React.SetStateAction<boolean>>
  rewindDetails: IRewindDetails
}

export const useRewindState = ({
  enabled,
  mediaUrlData,
  contentId,
  contentType,
}: {
  enabled: boolean
  mediaUrlData:
    | CurrentTvMediaUrlWithEpg
    | CurrentEventMediaUrlWithEpg
    | CurrentProgramMediaUrl
    | null
  contentId?: string | number | null
  contentType: VideoType
}): IRewindState => {
  const { rewoundUrlStartSec, rewoundTargetStartSec } = mediaUrlData ?? {}

  const [rewindType, setRewindType] = useState<RewindTypes | undefined>(
    undefined
  )
  const [isRewound, setIsRewound] = useState<boolean>(false)
  const [isRewindDelayed, setIsRewindDelayed] = useState<boolean>(false)
  const [rewindStamps, setRewindStamps] = useState<IRewindStamps>({
    rewoundUrlStartSec: 0,
    rewoundTargetStartSec: 0,
  })

  const {
    rewoundUrlStartSec: stateRewoundUrlStartSec,
    rewoundTargetStartSec: stateRewoundTargetStartSec,
  } = rewindStamps

  useEffect(() => {
    /*
     * Update / choose rewind type for current stream
     */
    if (!enabled) return

    if (isRewound || !mediaUrlData) return // rewind type should be changed only while 'at live edge'

    const { liveRewindOptions, scheduleType } = mediaUrlData

    setRewindType(
      chooseRewind({ liveRewindOptions, scheduleType, contentType })
    )
  }, [isRewound, enabled, mediaUrlData, contentType])

  useEffect(() => {
    /*
     * Update isRewound state if 'rewind url / live url' successfully fetched, so rewoundTargetStartSec value changed
     */
    if (!enabled) return

    if (
      typeof rewoundTargetStartSec !== 'number' ||
      typeof rewoundUrlStartSec !== 'number'
    )
      return

    if (rewoundTargetStartSec === 0 && rewoundUrlStartSec === 0) {
      setIsRewound(false)
      setRewindStamps({ rewoundUrlStartSec, rewoundTargetStartSec })
    }

    if (rewoundTargetStartSec > 0 && rewoundUrlStartSec > 0) {
      setIsRewound(true)
      setRewindStamps({ rewoundUrlStartSec, rewoundTargetStartSec })
    }
  }, [rewoundUrlStartSec, rewoundTargetStartSec, enabled])

  useEffect(() => {
    /*
     * Update isRewound state if contentId changed
     */

    setRewindStamps({ rewoundUrlStartSec: 0, rewoundTargetStartSec: 0 })
    setIsRewound(false)
    setIsRewindDelayed(false)
  }, [contentId, enabled])

  const rewindDetails = useMemo(
    () => ({
      rewoundUrlStartSec: stateRewoundUrlStartSec,
      rewoundTargetStartSec: stateRewoundTargetStartSec,
      rewindType,
    }),
    [stateRewoundUrlStartSec, stateRewoundTargetStartSec, rewindType]
  )

  return {
    isRewound,
    setIsRewound,
    isRewindDelayed,
    setIsRewindDelayed,
    rewindDetails,
  }
}
